import { useState, useEffect } from 'react'
import { IconButton, List, ListItemButton, Popover, Table, TableBody, TableContainer, TableHead, TableRow, TableCell, CircularProgress, ToggleButton, ToggleButtonGroup, Typography, Paper, Fade, ClickAwayListener } from '@mui/material'
import { Close, Settings } from '@mui/icons-material'
import './style.css'
import { AssignDialog, ReviewReportDialog, PDFFileDialog } from '../../dialogs'
import { useAuth, useReportsContext, useSitesContext, useUsersContext, useUser } from '../../context'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilePdf, faSync, faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import ReportColumnSelector from '../../components/reports/ReportColumnSelector'
import { useReportColumnPreferences } from '../../hooks/useReportColumnPreferences'
import ReportTableHeader from '../../components/reports/ReportTableHeader'
import ReportTableRow from '../../components/reports/ReportTableRow'
import { useReportActions } from '../../hooks/useReportActions'
import { pdfMakeRoutes, reportsRoutes } from '../../routes'

export const ReportsList = ({ list, typeReportsSelected, statusReports, tipo, setAllItemsSelected, allItemsSelected, selectItem, onExecuteOverride, canSelect }) => {
    const { userData } = useAuth()
    const { admin, isOperator, isUniversalChief, isUniversalInspector, isOperatorInspector, isSapExecutive, isShiftManager, isChiefMachinery } = useUser()
    const { sites } = useSitesContext()
    const { elevarReporte, getReports, valueFilter } = useReportsContext()
    const isSearching = !!(valueFilter && valueFilter.trim() !== '')
    const { usersMap } = useUsersContext()
    const { columnas, setColumnas, widthTotal, columnasParaMostrar } = useReportColumnPreferences(typeReportsSelected, tipo)
    const [listaParaMostrar, setListaParaMostrar] = useState(list)
    const [anchorPopoverEl, setAnchorPopoverEl] = useState(null)
    const [siteName, setSiteName] = useState([])
    const [anchorEl, setAnchorEl] = useState(null)
    const open = Boolean(anchorEl)
    const id = open ? 'simple-popover' : undefined
    const [allChecked, setAllChecked] = useState(false)
    const [indeterminatedChecked, setIndeterminatedChecked] = useState(false)
    const [openPdfDialog, setOpenPdfDialog] = useState(false)
    const [fileSelected, setFileSelected] = useState(null)
    const [showColumnSelector, setShowColumnSelector] = useState(false)

    const cacheBustUrl = (url) => {
        return `${url}?cb=${new Date().getTime()}`
    }

    const getViewModeStorageKey = () => {
        return 'global_reports_view_mode'
    }

    const [contextMenuPos, setContextMenuPos] = useState({ mouseX: null, mouseY: null })
    const [contextMenuItem, setContextMenuItem] = useState(null)
    const [viewMode, setViewMode] = useState(() => {
        const savedMode = localStorage.getItem(getViewModeStorageKey())
        return savedMode || 'default'
    })
    const {
        reportData,
        reportDataReview,
        openModalState,
        openReviewModalState,
        openAssignModal,
        closeAssignModal,
        openReviewModal,
        closeReviewModal,
        elevarReport
    } = useReportActions()

    useEffect(() => {
        setListaParaMostrar(list)
        const contadorDeChecks = list.filter((item) => { return item.checked })
        if (contadorDeChecks.length > 0 && contadorDeChecks.length === list.length) {
            setAllChecked(true)
            setIndeterminatedChecked(false)
        } else if (contadorDeChecks.length === 0) {
            setAllChecked(false)
            setIndeterminatedChecked(false)
        } else if (contadorDeChecks.length > 0 && contadorDeChecks.length < list.length) {
            setAllChecked(false)
            setIndeterminatedChecked(true)
        } else {
            setAllChecked(false)
            setIndeterminatedChecked(false)
        }
    }, [list])

    const handleClick = (event, reportItem) => {
        const obraObj = reportItem?.obra
        const siteId = obraObj?._id || reportItem?.site

        if (obraObj && typeof obraObj === 'object' && obraObj.descripcion) {
            setSiteName(obraObj.descripcion)
            setAnchorEl(event.currentTarget)
            return
        }
        const siteFiltered = sites.find((site) => site._id === siteId || site.idObra === siteId)
        if (siteFiltered) {
            setSiteName(siteFiltered.descripcion)
            setAnchorEl(event.currentTarget)
        } else {
            console.warn('[ReportsList] Site description not found for:', siteId)
        }
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const handleViewChange = (event, newView) => {
        if (newView !== null) {
            setViewMode(newView)
            localStorage.setItem(getViewModeStorageKey(), newView)

            if (newView === 'custom') {
                // Ensure at least some columns are checked if switching to custom and none are
                const hasChecked = columnas.some(c => c.checked)
                if (!hasChecked) {
                    const defaultCols = ['idIndex', 'machine', 'estado']
                    const newColumnas = columnas.map(c =>
                        defaultCols.includes(c.value) ? { ...c, checked: true } : c
                    )
                    setColumnas(newColumnas)
                }
            } else {
                setShowColumnSelector(false)
            }
        }
    }

    const handleContextMenu = (event, item) => {
        event.preventDefault()
        setContextMenuItem(item)
        setContextMenuPos({
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6
        })
    }

    const handleCloseContextMenu = () => {
        setContextMenuItem(null)
        setContextMenuPos({ mouseX: null, mouseY: null })
    }

    const [dots, setDots] = useState('.')
    useEffect(() => {
        if (!statusReports) return
        const interval = setInterval(() => {
            setDots((prev) => (prev.length >= 3 ? '.' : prev + '.'))
        }, 400)
        return () => clearInterval(interval)
    }, [statusReports])

    return (
        <div id="reportList" style={{
            width: '100%',
            height: '100%',
            paddingLeft: 10,
            paddingRight: 10,
            fontSize: 15,
            color: '#000',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column'
        }}>
            {/* View Mode Toggles & Column Button */}
            <div style={{ marginBottom: 10, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                {statusReports && (
                    <div style={{ display: 'flex', alignItems: 'center', marginRight: 'auto' }}>
                        <Typography variant="caption" style={{ color: '#757575', fontWeight: 500, fontSize: 13 }}>
                            Sincronizando reportes{dots}
                        </Typography>
                    </div>
                )}
                <ToggleButtonGroup
                    color="primary"
                    value={viewMode}
                    exclusive
                    onChange={handleViewChange}
                    size="small"
                    style={{ height: 30 }}
                >
                    <ToggleButton value="default" style={{ fontSize: 11, padding: '0px 10px' }}>General</ToggleButton>
                    <ToggleButton value="custom" style={{ fontSize: 11, padding: '0px 10px' }}>Personalizada</ToggleButton>
                </ToggleButtonGroup>

                {viewMode === 'custom' && (
                    <IconButton
                        size="small"
                        onClick={() => setShowColumnSelector(!showColumnSelector)}
                        color={showColumnSelector ? 'primary' : 'default'}
                        style={{ backgroundColor: showColumnSelector ? '#e3f2fd' : '#f5f5f5' }}
                        title="Configurar columnas"
                    >
                        <Settings fontSize="small" />
                    </IconButton>
                )}
            </div>

            {/* Floating Column Selector Panel */}
            {viewMode === 'custom' && showColumnSelector && (
                <ClickAwayListener onClickAway={() => setShowColumnSelector(false)}>
                    <Fade in={showColumnSelector}>
                        <Paper
                            elevation={4}
                            style={{
                                position: 'absolute',
                                right: 10,
                                top: 45,
                                zIndex: 1000,
                                width: 450,
                                maxHeight: 400,
                                overflow: 'hidden',
                                border: '1px solid #ddd',
                                borderRadius: 8
                            }}
                        >
                            <div style={{ padding: '8px 15px', backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>Configurar Columnas</Typography>
                                <IconButton size="small" onClick={() => setShowColumnSelector(false)}><Close fontSize="inherit" /></IconButton>
                            </div>
                            <div style={{ overflowY: 'auto', maxHeight: 340 }}>
                                <ReportColumnSelector
                                    columnas={columnas}
                                    setColumnas={setColumnas}
                                    typeReportsSelected={typeReportsSelected}
                                    embedded={true}
                                />
                            </div>
                        </Paper>
                    </Fade>
                </ClickAwayListener>
            )}

            {
                reportData && <AssignDialog open={openModalState} report={reportData} reportType={reportData.reportType} onlyClose={closeAssignModal} closeModal={closeAssignModal}/>
            }
            {
                reportDataReview && <ReviewReportDialog open={openReviewModalState} report={reportDataReview} onlyClose={closeReviewModal}/>
            }
            <PDFFileDialog
                open={openPdfDialog}
                handleClose={() => { setOpenPdfDialog(false); setFileSelected(null) }}
                file={fileSelected}
            />
            <Popover
                open={contextMenuPos.mouseX !== null}
                onClose={handleCloseContextMenu}
                {...(contextMenuPos.mouseX !== null && contextMenuPos.mouseY !== null
                    ? { anchorReference: 'mousePosition', anchorPosition: { top: contextMenuPos.mouseY, left: contextMenuPos.mouseX } }
                    : {}
                )}
            >
                <List>
                    <ListItemButton onClick={async () => {
                        if (contextMenuItem && contextMenuItem.urlPdf) {
                            handleCloseContextMenu()
                            try {
                                const response = await reportsRoutes.getPdfUrl(contextMenuItem._id)
                                if (response.data && response.data.url) {
                                    setFileSelected(cacheBustUrl(pdfMakeRoutes.viewPdf(response.data.url)))
                                    setOpenPdfDialog(true)
                                } else {
                                    alert('No se pudo obtener la URL del PDF. Por favor, intente regenerarlo.')
                                }
                            } catch (error) {
                                console.error('Error fetching PDF URL:', error)
                                alert('El enlace del PDF ha expirado o no se pudo renovar. Intente regenerar el PDF desde el detalle.')
                            }
                        } else {
                            alert('URL del PDF no disponible para vista previa.')
                            handleCloseContextMenu()
                        }
                    }}>
                        <FontAwesomeIcon icon={faFilePdf} style={{ marginRight: 8 }} /> Vista previa PDF
                    </ListItemButton>
                    <ListItemButton onClick={async () => {
                        if (contextMenuItem) {
                            handleCloseContextMenu()
                            try {
                                await pdfMakeRoutes.createPdfDoc(contextMenuItem)
                                await getReports()

                                // After regeneration, get the fresh URL
                                const response = await reportsRoutes.getPdfUrl(contextMenuItem._id)
                                if (response.data && response.data.url) {
                                    setFileSelected(cacheBustUrl(pdfMakeRoutes.viewPdf(response.data.url)))
                                    setOpenPdfDialog(true)
                                } else {
                                    alert('PDF regenerado, pero la URL no está disponible para vista previa. Recargue la página si el problema persiste.')
                                }
                                console.log('PDF regenerated successfully for:', contextMenuItem._id)
                            } catch (error) {
                                console.error('Error regenerating PDF:', error)
                                alert('Error al regenerar el PDF. Por favor, intente de nuevo.')
                            }
                        }
                    }}>
                        <FontAwesomeIcon icon={faSync} style={{ marginRight: 8 }} /> Volver a generar PDF
                    </ListItemButton>
                </List>
            </Popover>

            <TableContainer sx={{ flexGrow: 1, overflow: 'auto' }}>
                <Table stickyHeader aria-label="sticky table" style={{ minWidth: (viewMode === 'custom' && columnasParaMostrar.length > 0) ? widthTotal : '100%' }}>
                    <TableHead>
                        <ReportTableHeader
                            columnasParaMostrar={viewMode === 'custom' ? columnasParaMostrar : []}
                            admin={admin}
                            isOperator={isOperator}
                            isUniversalChief={isUniversalChief}
                            isUniversalInspector={isUniversalInspector}
                            isOperatorInspector={isOperatorInspector}
                            allChecked={allChecked}
                            indeterminatedChecked={indeterminatedChecked}
                            setAllItemsSelected={(checked) => {
                                setAllItemsSelected(checked)
                                setIndeterminatedChecked(false)
                            }}
                            typeReportsSelected={typeReportsSelected}
                            onColumnSelectorClick={() => setShowColumnSelector(!showColumnSelector)} // Toggle floating panel
                            widthTotal={widthTotal}
                            tipo={tipo}
                            isSearching={isSearching}
                            canSelect={canSelect}
                        />
                    </TableHead>
                    <TableBody>
                        {
                            (listaParaMostrar && listaParaMostrar.length > 0)
                                ? listaParaMostrar.map((item, index) => (
                                    <ReportTableRow
                                        key={item._id || item.idIndex || index}
                                        item={item}
                                        index={index}
                                        columnasParaMostrar={viewMode === 'custom' ? columnasParaMostrar : []}
                                        admin={admin}
                                        isOperator={isOperator}
                                        isUniversalChief={isUniversalChief}
                                        isUniversalInspector={isUniversalInspector}
                                        isOperatorInspector={isOperatorInspector}
                                        isSapExecutive={isSapExecutive}
                                        isShiftManager={isShiftManager}
                                        isChiefMachinery={isChiefMachinery}
                                        typeReportsSelected={typeReportsSelected}
                                        selectItem={selectItem}
                                        openModal={openAssignModal}
                                        openReviewModal={onExecuteOverride || openReviewModal}
                                        elevar={elevarReport}
                                        usersMap={usersMap}
                                        tipo={tipo}
                                        handleSiteClick={handleClick}
                                        handleSiteClose={handleClose}
                                        siteAnchorEl={anchorEl}
                                        isSitePopoverOpen={open}
                                        sitePopoverId={id}
                                        siteNameDescription={siteName}
                                        onContextMenu={(event) => handleContextMenu(event, item)}
                                        isSearching={isSearching}
                                        canSelect={canSelect}
                                    />
                                ))
                                : (
                                    <TableRow>
                                        <TableCell colSpan={50} align="center" sx={{ padding: '100px 20px', border: 'none' }}>
                                            {
                                                statusReports
                                                    ? (
                                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                            <CircularProgress size={40} sx={{ mb: 2 }} />
                                                            <p style={{ fontSize: 18, color: '#666' }}>Descargando la información <br /> Espere un momento.</p>
                                                        </div>
                                                    )
                                                    : (
                                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                            <FontAwesomeIcon icon={faInfoCircle} size="3x" style={{ color: '#ccc', marginBottom: 20 }} />
                                                            <p style={{ fontSize: 18, color: '#666' }}>Selecciona otra opción <br /> El detalle no cuenta con lista de reportes.</p>
                                                        </div>
                                                    )
                                            }
                                        </TableCell>
                                    </TableRow>
                                )
                        }
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    )
}
